import React, { useEffect, useState } from 'react';
import { Form, Input, Select, DatePicker, Button, Divider, Modal, message } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, CloseOutlined, CameraOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WEIGHT_UNITS } from '../../utils/constants';
import { getAccountsApi, createAccountApi, getAccountByIdApi } from '../../api/accounts';
import { createSellingApi, updateSellingApi } from '../../api/sales';
import { decryptData } from '../../utils/crypto';
import dayjs from 'dayjs';

const RATE_UNITS = ['per kg', 'per quintal', 'per ton', 'per man'];
const WEIGHT_TO_KG = { '1': 1, '2': 20, '3': 21, '4': 100, '5': 1000 };
const RATE_UNIT_KG = { 'per kg': 1, 'per quintal': 100, 'per ton': 1000, 'per man': 20 };

const PAYMENT_OPTIONS = [
  { value: 'credit',  label: 'Received' },
  { value: 'partial', label: 'Partial' },
  { value: 'debit',   label: 'Outstanding' },
];

function calcTotal(weight, weightUnit, rate, rateUnit) {
  if (!weight || !weightUnit || !rate || !rateUnit) return null;
  const weightKg = Number(weight) * (WEIGHT_TO_KG[String(weightUnit)] || 1);
  const rateBase = RATE_UNIT_KG[rateUnit] || 1;
  return Math.round((weightKg / rateBase) * Number(rate) * 100) / 100;
}

const fieldLabel = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const hintStyle = { fontSize: 12, color: '#9ca3af', fontWeight: 400, marginLeft: 6 };

function PhotoPicker({ preview, label, hintText, onChange, onClear }) {
  return (
    <div>
      {label && (
        <div style={fieldLabel}>
          {label}{hintText && <span style={hintStyle}>{hintText}</span>}
        </div>
      )}
      <label style={{ display: 'block', cursor: 'pointer' }}>
        {preview ? (
          <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <img src={preview} alt="preview" style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.45)', padding: '6px 0', textAlign: 'center' }}>
              <span style={{ color: '#fff', fontSize: 12, fontWeight: 500 }}>Click to change</span>
            </div>
            {onClear && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); onClear(); }}
                style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 26, height: 26, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <CloseOutlined style={{ fontSize: 11 }} />
              </button>
            )}
          </div>
        ) : (
          <div style={{ border: '2px dashed #d1d5db', borderRadius: 10, background: '#fafafa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 100, gap: 8 }}>
            <CameraOutlined style={{ fontSize: 24, color: '#9ca3af' }} />
            <span style={{ fontSize: 12, color: '#9ca3af' }}>Click to upload photo</span>
          </div>
        )}
        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onChange} />
      </label>
    </div>
  );
}

export default function AddSalePage() {
  const navigate = useNavigate();
  const { cropId } = useParams();
  const location = useLocation();
  const initialValues = location.state?.editData ?? null;
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [photoPreview, setPhotoPreview]               = useState(null);
  const [photoFile, setPhotoFile]                     = useState(null);
  const [paymentImagePreview, setPaymentImagePreview] = useState(null);
  const [paymentImageFile, setPaymentImageFile]       = useState(null);
  const [allAccounts, setAllAccounts]                 = useState([]);
  const [bankBalances, setBankBalances]               = useState({});
  const [addBuyerOpen, setAddBuyerOpen]               = useState(false);
  const [newBuyer, setNewBuyer]                       = useState({ name: '', mobile: '' });
  const [addBuyerLoading, setAddBuyerLoading]         = useState(false);
  const [saving, setSaving]                           = useState(false);

  const buyers       = allAccounts.filter((a) => a.type === 'buyer' && a.name?.toLowerCase() !== 'default' && a.name !== null);
  const bankAccounts = allAccounts.filter((a) => a.type === 'bank');

  const paymentStatus  = Form.useWatch('payment_status', form);
  const showBankPicker = bankAccounts.length > 0 && (paymentStatus === 'credit' || paymentStatus === 'partial');

  useEffect(() => {
    getAccountsApi()
      .then((res) => {
        const accs = res.data?.data || res.data || [];
        setAllAccounts(accs);
        const bankAccs = accs.filter((a) => a.type === 'bank');
        Promise.all(bankAccs.map((a) => getAccountByIdApi(a.id, { type: 'bank' }).then((r) => {
          const txns = r.data?.transactions || [];
          const credit = txns.filter((t) => t.payment_status === 'credit').reduce((s, t) => s + Number(t.amount), 0);
          const debit  = txns.filter((t) => t.payment_status === 'debit').reduce((s, t) => s + Number(t.amount), 0);
          return { id: a.id, balance: credit - debit };
        }).catch(() => ({ id: a.id, balance: null })))).then((results) => {
          setBankBalances(Object.fromEntries(results.map((r) => [r.id, r.balance])));
        });
      })
      .catch(() => {});

    form.setFieldsValue(
      initialValues
        ? { ...initialValues, date: initialValues.date ? dayjs(initialValues.date) : dayjs(), payment_status: initialValues.payment_status || 'credit' }
        : { date: dayjs(), weightUnit: '4', rateUnit: 'per quintal', payment_status: 'credit' }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddBuyer = async () => {
    if (!newBuyer.name.trim()) return;
    setAddBuyerLoading(true);
    try {
      const res = await createAccountApi({ name: newBuyer.name.trim(), mobile: newBuyer.mobile.trim(), type: 'buyer' });
      const created = res.data?.data || res.data;
      setAllAccounts((prev) => [...prev, created]);
      form.setFieldValue('buyer_id', created.id);
      setAddBuyerOpen(false);
      setNewBuyer({ name: '', mobile: '' });
    } catch {
      message.error('Failed to add buyer');
    } finally {
      setAddBuyerLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      let buyerId = values.buyer_id ?? null;
      if (!buyerId) {
        let defaultBuyer = allAccounts.find((a) => a.type === 'buyer' && a.name?.toLowerCase() === 'default');
        if (!defaultBuyer) {
          try {
            const res = await createAccountApi({ name: 'default', type: 'buyer' });
            defaultBuyer = res.data?.data || res.data;
            if (defaultBuyer) setAllAccounts((prev) => [...prev, defaultBuyer]);
          } catch {}
        }
        if (defaultBuyer) buyerId = defaultBuyer.id;
      }

      setSaving(true);
      const fd = new FormData();
      Object.entries(values).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          fd.append(k, k === 'date' ? v.format('YYYY-MM-DD') : v);
        }
      });
      if (buyerId && !values.buyer_id) fd.append('buyer_id', buyerId);
      fd.append('cropId', cropId);
      if (initialValues?.id) fd.append('id', initialValues.id);
      if (photoFile) fd.append('photo', photoFile);
      if (!initialValues?.id && paymentImageFile) fd.append('payment_image', paymentImageFile);

      if (initialValues?.id) {
        await updateSellingApi(fd);
        message.success('Sale updated!');
      } else {
        await createSellingApi(fd);
        message.success('Sale added!');
      }
      sessionStorage.setItem(`cropTab_${cropId}`, 'sales');
      navigate(-1);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(initialValues?.id ? 'Failed to update sale' : 'Failed to add sale');
    } finally {
      setSaving(false);
    }
  };

  const bankOptions  = bankAccounts.map((a) => {
    const dec = a.bank_details ? (decryptData(a.bank_details) || {}) : {};
    const balance = bankBalances[a.id] ?? null;
    return { value: a.id, label: (a.name || dec.bank_name || 'Bank Account') + (a.name && dec.bank_name ? ` — ${dec.bank_name}` : ''), balance };
  });
  const buyerOptions = buyers.map((b) => ({ value: b.id, label: b.name }));

  return (
    <>
      <div style={{ margin: -24, minHeight: 'calc(100vh - 64px)', background: '#fff' }}>

        {/* ── Sticky page header ── */}
        <div style={{
          position: 'sticky', top: 64, zIndex: 100,
          background: '#fff',
          borderBottom: '1px solid #e5e7eb',
          padding: '0 40px',
          height: 56,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, color: '#6b7280', display: 'flex', alignItems: 'center' }}
            >
              <ArrowLeftOutlined style={{ fontSize: 15 }} />
            </button>
            <span style={{ color: '#9ca3af', fontSize: 13 }}>/</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
              {initialValues?.id ? 'Edit Sale' : 'Add Sale'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave} style={{ background: '#1a6b2a', borderColor: '#1a6b2a', fontWeight: 600 }}>
              Save
            </Button>
          </div>
        </div>

        {/* ── Form body ── */}
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 40px 80px' }}>

          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
            {initialValues?.id ? 'Edit Sale' : 'Add Sale'}
          </h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 36px' }}>
            Record the crop sale details below.
          </p>

          <Form
            form={form}
            layout="vertical"
            size="large"
            requiredMark={false}
            onValuesChange={(changed, all) => {
              if ('weight' in changed || 'weightUnit' in changed || 'rate' in changed || 'rateUnit' in changed) {
                const total = calcTotal(all.weight, all.weightUnit, all.rate, all.rateUnit);
                if (total !== null) form.setFieldValue('amount', total);
              }
            }}
          >
            {/* ── Quantity & Rate ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
              <Form.Item label={<span style={fieldLabel}>{t('quantity')}</span>} name="weight" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                <Input type="number" placeholder="e.g. 10" style={{ borderRadius: 8 }} />
              </Form.Item>
              <Form.Item label={<span style={fieldLabel}>Weight Unit</span>} name="weightUnit" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                <Select options={Object.entries(WEIGHT_UNITS).map(([k, v]) => ({ value: k, label: v }))} />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
              <Form.Item label={<span style={fieldLabel}>{t('rate')}</span>} name="rate" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                <Input type="number" placeholder="e.g. 2500" style={{ borderRadius: 8 }} />
              </Form.Item>
              <Form.Item label={<span style={fieldLabel}>Rate Unit</span>} name="rateUnit" style={{ marginBottom: 0 }}>
                <Select options={RATE_UNITS.map((r) => ({ value: r, label: r }))} />
              </Form.Item>
            </div>

            <Divider style={{ margin: '0 0 28px' }} />

            {/* ── Amount & Payment ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
              <Form.Item label={<span style={fieldLabel}>Total Amount (₹)</span>} name="amount" style={{ marginBottom: 0 }}>
                <Input type="number" placeholder="Auto-calculated" style={{ borderRadius: 8 }} />
              </Form.Item>
              <Form.Item label={<span style={fieldLabel}>Payment Status</span>} name="payment_status" style={{ marginBottom: 0 }}>
                <Select options={PAYMENT_OPTIONS} onChange={(v) => { if (v === 'debit') form.setFieldValue('bank_account_id', null); }} />
              </Form.Item>
            </div>

            {paymentStatus === 'partial' && (
              <div style={{ marginBottom: 28 }}>
                <label style={fieldLabel}>Received Amount <span style={hintStyle}>amount received from buyer so far</span></label>
                <Form.Item name="received_amount" rules={[{ required: true, message: 'Enter received amount' }]} style={{ marginBottom: 0 }}>
                  <Input type="number" placeholder="0" prefix="₹" style={{ borderRadius: 8 }} />
                </Form.Item>
              </div>
            )}

            <div style={{ marginBottom: 28 }}>
              <label style={fieldLabel}>Buyer <span style={hintStyle}>person you sold to</span></label>
              <Form.Item name="buyer_id" style={{ marginBottom: 0 }}>
                <Select
                  showSearch allowClear optionFilterProp="label"
                  placeholder="Select buyer"
                  options={buyerOptions}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div style={{ padding: '4px 8px', borderTop: '1px solid #f0f0f0' }}>
                        <Button type="link" icon={<PlusOutlined />} size="small" style={{ color: '#1a6b2a', padding: 0 }} onMouseDown={(e) => { e.preventDefault(); setAddBuyerOpen(true); }}>
                          Add new buyer
                        </Button>
                      </div>
                    </>
                  )}
                />
              </Form.Item>
            </div>

            {showBankPicker && (
              <div style={{ marginBottom: 28 }}>
                <label style={fieldLabel}>Bank Account <span style={hintStyle}>account where you received money</span></label>
                <Form.Item name="bank_account_id" style={{ marginBottom: 0 }}>
                  <Select
                    allowClear placeholder="Select bank account (optional)" options={bankOptions}
                    optionRender={(opt) => (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{opt.data.label}</span>
                        {opt.data.balance !== null && (
                          <span style={{ fontSize: 12, fontWeight: 600, color: opt.data.balance >= 0 ? '#16a34a' : '#ef4444' }}>
                            {opt.data.balance >= 0 ? '' : '−'}₹{Math.abs(opt.data.balance).toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </Form.Item>
              </div>
            )}

            <Divider style={{ margin: '0 0 28px' }} />

            {/* ── Date & Note ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
              <Form.Item label={<span style={fieldLabel}>{t('date')}</span>} name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                <DatePicker style={{ width: '100%', borderRadius: 8 }} />
              </Form.Item>
              <Form.Item label={<span style={fieldLabel}>Note <span style={hintStyle}>optional</span></span>} name="note" style={{ marginBottom: 0 }}>
                <Input placeholder="Optional note" style={{ borderRadius: 8 }} />
              </Form.Item>
            </div>

            {/* ── Photos ── */}
            <div style={{ display: 'grid', gridTemplateColumns: initialValues?.id ? '1fr' : '1fr 1fr', gap: 20, marginBottom: 40 }}>
              <PhotoPicker
                label="Product Photo"
                hintText="optional"
                preview={photoPreview}
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setPhotoFile(file);
                  setPhotoPreview(file ? URL.createObjectURL(file) : null);
                  e.target.value = '';
                }}
                onClear={() => { setPhotoFile(null); setPhotoPreview(null); }}
              />
              {!initialValues?.id && (
                <PhotoPicker
                  label="Payment Receipt"
                  hintText="optional"
                  preview={paymentImagePreview}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setPaymentImageFile(file);
                    setPaymentImagePreview(file ? URL.createObjectURL(file) : null);
                    e.target.value = '';
                  }}
                  onClear={() => { setPaymentImageFile(null); setPaymentImagePreview(null); }}
                />
              )}
            </div>

          </Form>

          {/* ── Actions ── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
            <Button size="large" onClick={() => navigate(-1)} style={{ minWidth: 100, borderRadius: 8 }}>Cancel</Button>
            <Button size="large" type="primary" loading={saving} onClick={handleSave} style={{ background: '#1a6b2a', borderColor: '#1a6b2a', minWidth: 120, borderRadius: 8, fontWeight: 600 }}>
              Save Sale
            </Button>
          </div>

        </div>
      </div>

      <Modal
        title="Add New Buyer"
        open={addBuyerOpen}
        onCancel={() => { setAddBuyerOpen(false); setNewBuyer({ name: '', mobile: '' }); }}
        onOk={handleAddBuyer}
        confirmLoading={addBuyerLoading}
        okText="Add"
        okButtonProps={{ style: { background: '#1a6b2a', borderColor: '#1a6b2a' } }}
        width={380}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          <Input placeholder="Buyer name *" value={newBuyer.name} onChange={(e) => setNewBuyer((p) => ({ ...p, name: e.target.value }))} autoFocus />
          <Input placeholder="Mobile number" value={newBuyer.mobile} onChange={(e) => setNewBuyer((p) => ({ ...p, mobile: e.target.value }))} onPressEnter={handleAddBuyer} />
        </div>
      </Modal>
    </>
  );
}
